import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateCardPaymentRequest,
  CreateCcChargeRequest,
  CloseCardCycleRequest,
  UpdateCcChargeRequest,
  UpsertCreditCardRequest,
} from '@financial/shared';
import type { AppCurrency } from '@financial/shared';
import {
  closeCycle,
  createCharge,
  createPayment,
  deleteCharge,
  deletePayment,
  getCard,
  getInstallments,
  getProjection,
  listCharges,
  listPayments,
  updateCharge,
  upsertCard,
} from './api';
import { notify } from '../../store/notifications';

const CARD = ['credit-card'];
const CHARGES = ['credit-card', 'charges'];
const INSTALLMENTS = ['credit-card', 'installments'];
const PROJECTION = ['credit-card', 'projection'];
const PAYMENTS = ['credit-card', 'payments'];

export function useCard(currency?: AppCurrency) {
  return useQuery({ queryKey: [...CARD, currency ?? 'all'], queryFn: () => getCard(currency) });
}
export function useCharges() {
  return useQuery({ queryKey: CHARGES, queryFn: listCharges });
}
export function useInstallments() {
  return useQuery({ queryKey: INSTALLMENTS, queryFn: getInstallments });
}
export function useProjection(enabled: boolean) {
  return useQuery({ queryKey: PROJECTION, queryFn: getProjection, enabled });
}

export function usePayments() {
  return useQuery({ queryKey: PAYMENTS, queryFn: listPayments });
}

function useInvalidateAll() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['credit-card'] });
    // Un pago mueve el saldo de la cuenta y el presupuesto del mes.
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['net-worth'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
    void qc.invalidateQueries({ queryKey: ['insights'] });
  };
}

export function useUpsertCard() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: UpsertCreditCardRequest) => upsertCard(body),
    onSuccess: () => {
      invalidate();
      notify.success('Tarjeta actualizada');
    },
  });
}

export function useCreateCharge() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: CreateCcChargeRequest) => createCharge(body),
    onSuccess: (charge) => {
      invalidate();
      notify.success('Cargo registrado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await deleteCharge(charge.id);
            invalidate();
            notify.info('Cargo deshecho');
          },
        },
      });
    },
  });
}

export function useUpdateCharge() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCcChargeRequest }) =>
      updateCharge(id, body),
    onSuccess: () => {
      invalidate();
      notify.success('Cargo actualizado');
    },
  });
}

export function useDeleteCharge() {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => deleteCharge(id), onSuccess: invalidate });
}

export function useCloseCycle() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: CloseCardCycleRequest) => closeCycle(body),
    onSuccess: () => {
      invalidate();
      notify.success('Pago del corte confirmado');
    },
  });
}

export function useCreatePayment() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: CreateCardPaymentRequest) => createPayment(body),
    onSuccess: (payment) => {
      invalidate();
      notify.success('Pago de tarjeta registrado', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await deletePayment(payment.id);
            invalidate();
            notify.info('Pago de tarjeta deshecho');
          },
        },
      });
    },
  });
}

export function useDeletePayment() {
  const invalidate = useInvalidateAll();
  return useMutation({ mutationFn: (id: string) => deletePayment(id), onSuccess: invalidate });
}
